/** Ably 事件名，同時當成轉發到 document 的 CustomEvent 名，兩支檔案共用同一份字串。 */
export const QUEUE_SUMMONED_EVENT = 'queue_summoned';
export const QUEUE_UPDATED_EVENT = 'queue_updated';

const POLL_TIER_MS = { front: 10000, near: 30000, far: 60000 };

/**
 * 已過期的 deadline 的重抓下限。到期當下 server 端 advance() 用的是 0 秒鎖等待，
 * 搶不到鎖就整段跳過，回來的 snapshot 仍是同一個過期狀態；沒有這個下限，
 * 延遲會是 0 而變成連續打 admin-ajax，直到鎖被釋放為止。
 */
const EXPIRED_DEADLINE_FLOOR_MS = 1000;

/** snapshot 的時間戳一律是秒，畫面計算一律毫秒。 */
function deadlineMsOf(snapshot) {
    if (snapshot?.state === 'summoned' && snapshot.ack_expires_at != null) {
        return snapshot.ack_expires_at * 1000;
    }
    if (snapshot?.state === 'active' && snapshot.expires_at != null) {
        return snapshot.expires_at * 1000;
    }
    return null;
}

/**
 * 依序位分層；summoned / active 靠本地倒數到點才重抓，kicked / not_in_queue 根本不在排隊，
 * 四者都回 null（不 poll）。
 *
 * @param {object|null|undefined} snapshot
 * @returns {number|null}
 */
export function pollIntervalMs(snapshot) {
    if (snapshot?.state !== 'in_queue') {
        return null;
    }

    const position = snapshot.position;
    if (typeof position !== 'number') {
        return POLL_TIER_MS.far;
    }
    if (position <= 1) {
        return POLL_TIER_MS.front;
    }
    if (position <= 3) {
        return POLL_TIER_MS.near;
    }
    return POLL_TIER_MS.far;
}

/**
 * 下次該重抓的毫秒延遲：poll 分層與「ack 到期／窗口到期」取較小值。
 * 已過期的 deadline 夾到 EXPIRED_DEADLINE_FLOOR_MS，不會是 0 或負數。
 * 兩者皆無（不在排隊、已過號）回 null。
 *
 * @param {object|null|undefined} snapshot
 * @param {number} nowCorrectedMs 已套用時鐘偏移的當下毫秒
 * @returns {number|null}
 */
export function nextRefetchDelayMs(snapshot, nowCorrectedMs) {
    const candidates = [];

    const poll = pollIntervalMs(snapshot);
    if (poll !== null) {
        candidates.push(poll);
    }

    const deadlineMs = deadlineMsOf(snapshot);
    if (deadlineMs !== null) {
        candidates.push(Math.max(EXPIRED_DEADLINE_FLOOR_MS, deadlineMs - nowCorrectedMs));
    }

    return candidates.length === 0 ? null : Math.min(...candidates);
}

/**
 * 輪到自己才動得了號碼：叫號中已在自己的窗口內，與輪到你同待遇。
 *
 * 納入 summoned 是刻意放寬——auto-ack 幾乎立刻把它轉成 active，另做一套停用外觀只會讓
 * 畫面閃一下。代價是 ack 失敗時（狀態停在 summoned）使用者最長有一個叫號窗口的時間可以在
 * 未 active 的狀態下送出抽獎，而後端目前沒有「抽獎必須 active」的閘門擋這一段。
 */
const PICKING_ALLOWED_STATES = ['summoned', 'active'];

/**
 * 排隊商品且還沒輪到自己 → 鎖住選號與抽獎。非排隊商品行為完全不變。
 *
 * 選號工具、號碼盤與主按鈕共用這一條判定，分開寫遲早會漂移成三套規則。
 *
 * @param {object|null|undefined} view queueViewState() 的結論
 * @returns {{disabled: boolean, reason: (string|null)}}
 */
export function drawGateState(view) {
    if (!view || !view.hasQueue || PICKING_ALLOWED_STATES.includes(view.state)) {
        return { disabled: false, reason: null };
    }

    return { disabled: true, reason: '此商品需排隊，輪到你時才能抽獎' };
}

/** @returns {boolean} */
export function shouldAutoAck(snapshot) {
    return snapshot?.state === 'summoned';
}

/** 倒數進入這個秒數以內就換警示配色，自己的抽獎時間與本輪剩餘都適用。 */
const WARNING_THRESHOLD_SECONDS = 60;

function secondsUntil(epochSeconds, nowCorrectedMs) {
    return Math.max(0, Math.floor((epochSeconds * 1000 - nowCorrectedMs) / 1000));
}

const NO_COUNTDOWN = { owner: null, deadline: null };

/**
 * 倒數的歸屬與截止時間。active 看的是自己的窗口，其餘態看的是目前佔位者這一輪；
 * 過號者兩者皆無——他既沒有自己的窗口，也不該被別人的倒數牽著跑。
 *
 * 叫號中一樣沒有倒數。那 30 秒的窗口是給 server 確認人還在的，不是使用者的等待時間；
 * 畫成倒數只會在正要開始抽獎的人面前跳出一段與他無關的秒數。這段改顯示固定文案，
 * 由 `queueViewState()` 的 isJoining 帶出去。這裡連本輪倒數也一併擋掉——被叫號的人
 * 已經拿到位置，上一輪的殘值對他沒有意義。
 *
 * 別人那一輪過期後就不再是倒數擁有者：那一輪在無人打擾下自然落幕時沒有任何重抓排程
 * （`nextRefetchDelayMs` 對 not_in_queue 回 null，server 的 advance 也是 lazy 的），
 * 少了這道閘，停在頁面上的訪客會看到常駐的「0 秒」並背著一顆永不停的每秒 interval。
 * 自己的窗口不必比照——過期後 `nextRefetchDelayMs` 會在下限秒數內補抓，0 秒只閃一下。
 */
function countdownOf(snapshot, nowCorrectedMs) {
    if (snapshot?.state === 'summoned') {
        return NO_COUNTDOWN;
    }
    if (snapshot?.state === 'active' && snapshot.expires_at != null) {
        return { owner: 'self', deadline: snapshot.expires_at };
    }
    if (snapshot?.state !== 'kicked' && snapshot?.current_round_expires_at != null) {
        return snapshot.current_round_expires_at * 1000 > nowCorrectedMs
            ? { owner: 'round', deadline: snapshot.current_round_expires_at }
            : NO_COUNTDOWN;
    }

    return NO_COUNTDOWN;
}

/**
 * 進度條分母。取「單次消費最大保留」與「首抽」的大者——後台兩個欄位各自獨立且無交叉驗證，
 * 取小者會讓進度條一開始就缺一截。沒有倒數擁有者就沒有進度條，分母一併為 null。
 */
function totalSecondsOf(snapshot, owner) {
    if (owner === null) {
        return null;
    }

    const windowSeconds = snapshot?.window_seconds;
    const maxRoundSeconds = snapshot?.max_round_seconds;
    if (windowSeconds == null || maxRoundSeconds == null) {
        return null;
    }

    return Math.max(maxRoundSeconds, windowSeconds);
}

/**
 * 兩條列共用的排隊檢視狀態：排隊態、順位、倒數、分母、警示一次算完。
 * 資訊列與底部列都只從這裡讀，不各自重算倒數，畫面才不會互相漂移。
 *
 * @param {object|null|undefined} snapshot 隊列快照，首次渲染前為 null
 * @param {boolean} queueEnabled 本商品是否啟用排隊機制
 * @param {number} nowCorrectedMs 已套用時鐘偏移的當下毫秒
 * @returns {{
 *   hasQueue: boolean,
 *   state: ('not_in_queue'|'in_queue'|'summoned'|'active'|'kicked'),
 *   position: (number|null), waitingCount: number,
 *   countdownOwner: ('self'|'round'|null),
 *   remainingSeconds: (number|null), totalSeconds: (number|null),
 *   progressPercent: (number|null), isWarning: boolean,
 *   isJoining: boolean
 * }}
 */
export function queueViewState(snapshot, queueEnabled, nowCorrectedMs) {
    const hasQueue = Boolean(queueEnabled);
    const waitingCount = snapshot?.waiting_count ?? 0;

    if (!hasQueue) {
        return {
            hasQueue: false,
            state: 'not_in_queue',
            position: null,
            waitingCount,
            countdownOwner: null,
            remainingSeconds: null,
            totalSeconds: null,
            progressPercent: null,
            isWarning: false,
            isJoining: false,
        };
    }

    const state = snapshot?.state ?? 'not_in_queue';
    const position = state === 'in_queue' ? (snapshot?.position ?? null) : null;
    const { owner, deadline } = countdownOf(snapshot, nowCorrectedMs);

    const remainingSeconds = owner === null ? null : secondsUntil(deadline, nowCorrectedMs);
    const totalSeconds = totalSecondsOf(snapshot, owner);
    const progressPercent = remainingSeconds === null || !totalSeconds
        ? null
        : Math.min(100, Math.max(0, (remainingSeconds / totalSeconds) * 100));

    return {
        hasQueue: true,
        state,
        position,
        waitingCount,
        countdownOwner: owner,
        remainingSeconds,
        totalSeconds,
        progressPercent,
        isWarning: remainingSeconds !== null && remainingSeconds <= WARNING_THRESHOLD_SECONDS,
        // 名字對齊它驅動的那句文案，不是對齊路徑。排隊十分鐘後才被叫號的人也吃這個旗標——
        // 他同樣正在被安置進本輪，只是等待的那段已經過去了。兩條路徑共用同一句是議定的。
        isJoining: state === 'summoned',
    };
}

/**
 * 誰需要為這則 queue_updated 廣播重抓 snapshot。
 * in_queue 的序位一定要重抓；summoned 比對法失效（上一輪已收掉，本地與廣播的本輪到期時間
 * 都是 null），無條件重抓——同一商品同時只有一位 summoned，窗口僅 30 秒，成本極低。
 * active 與 not_in_queue 都比對廣播帶來的本輪到期時間與本地是否相同：active 本人就是本輪
 * 佔位者，兩個欄位同源，值一變必定同時變；not_in_queue 換輪提早結束（leaveHint 縮窗、主動
 * leave）時 waiting_count 可能不變，但這個值一定會變。兩邊相同時不重抓，長窗口期間的
 * join/leave 廣播若沒真的換輪，idle 訪客與本輪佔位者都不必白打一次 admin-ajax。
 *
 * @param {object|null|undefined} snapshot
 * @param {number|null|undefined} broadcastCurrentRoundExpiresAt queue_updated 廣播帶來的本輪到期時間（秒）；
 *     未帶這個 key 的廣播（舊格式）會是 undefined，與 null 等價處理
 * @returns {boolean}
 */
export function shouldRefetchOnQueueUpdated(snapshot, broadcastCurrentRoundExpiresAt) {
    switch (snapshot?.state) {
        case 'in_queue':
        case 'summoned':
            return true;
        case 'active':
        case 'not_in_queue':
            return (snapshot.current_round_expires_at ?? null) !== (broadcastCurrentRoundExpiresAt ?? null);
        default:
            return false;
    }
}
